"use client";

import { FC, useState, useRef, useLayoutEffect } from "react";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { useStyles } from "./styles";
import { Tag as TagType } from "@/types/thread";
import { sortTags } from "./helpers";
import Tag from "@/components/common/Tag";

interface TagsProps {
  tags: TagType[];
  className?: string;
}

const Tags: FC<TagsProps> = ({ tags, className }) => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const tagsRef = useRef<HTMLDivElement>(null);
  const singleRowHeight = 32;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useLayoutEffect(() => {
    const checkHeight = () => {
      const current = tagsRef.current;
      if (current) {
        const totalContentHeight = current.scrollHeight;
        const visibleContentHeight = current.clientHeight;

        if (totalContentHeight > visibleContentHeight) {
          setShowExpandButton(true);
        } else {
          setShowExpandButton(false);
        }
      }
    };

    checkHeight();
    window.addEventListener("resize", checkHeight);
    return () => {
      window.removeEventListener("resize", checkHeight);
    };
  }, [tags]);

  const displayTags = sortTags(tags);

  return (
    <div className={className || classes.container}>
      <div
        ref={tagsRef}
        className={classes.tags}
        style={{
          maxHeight: expanded ? "none" : singleRowHeight,
          marginBottom: expanded ? "1em" : 0,
        }}
      >
        {displayTags.map((tag, index) => (
          <Tag {...tag} key={`tag-${index + 1}`} />
        ))}
      </div>
      {showExpandButton && (
        <IconButton
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          className={classes.expandButton}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      )}
    </div>
  );
};

export default Tags;
